import PeopleIcon from '@mui/icons-material/People';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import {
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CalendarList from './CalendarList';
import HighlightedAppointment from 'FRONTEND/components/Calendar/HighlightedAppointment';
import AmbulanceIcon from 'FRONTEND/components/Geolocation/AmbulanceIcon/AmbulanceIcon';
import InitialsAvatar from 'FRONTEND/components/InitialsAvatar';
import SidePanel from 'FRONTEND/components/SidePanel';
import { resetBookingForm } from 'FRONTEND/reducers/bookingSlice';
import {
  goToActorCalendar,
  goToActorSettings,
  goToGivenExpertForm,
  goToMultipleCalendar,
  toggleAppointmentModal,
} from 'FRONTEND/reducers/calendarSlice';
import { changeExpertFilterText } from 'FRONTEND/reducers/expertSlice';
import { changeResourceFilterText, selectFilteredResources, selectTotalResources } from 'FRONTEND/reducers/resourceSlice';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import { selectCalendarActor } from 'FRONTEND/selectors/calendarSelectors';
import { selectFilteredManagedExperts, unfilteredManagedExpertCount } from 'FRONTEND/selectors/expertSelectors';
import { CALENDAR_MINIMUM_ITEMS_TO_SHOW_SEARCH, EXPERT, RESOURCE } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { getInitials } from 'FRONTEND/utils/initials';
import { RIGHTS } from 'FRONTEND/utils/rights';
import {
  CALENDAR_PATH,
  CALENDAR_USER_SETTINGS_PATH,
} from 'FRONTEND/utils/routes';
import { isSpecialKey } from 'LEGACY/compatibility';
import { PJAX_MAIN_CONTAINER_SELECTOR } from 'LEGACY/constants';

// Tab type identifiers
const TAB_TYPES = {
  MULTI_CALENDAR: 'multi_calendar',
};

const getStaticTabList = (hasManagementRight, hasCalendarNavigateRight) => {
  const tabs = [];

  if (hasCalendarNavigateRight) {
    tabs.push({
      type: TAB_TYPES.MULTI_CALENDAR,
      label: hasManagementRight ? i18n._('Multiple view') : i18n._('My calendar'),
      href: CALENDAR_PATH,
      routes: [CALENDAR_PATH, CALENDAR_USER_SETTINGS_PATH],
      icon: <Tooltip title={hasManagementRight ? i18n._('Multiple view') : i18n._('My calendar')} placement='right'><RecentActorsIcon /></Tooltip>,
    });
  }
  return tabs;
};

// The single calendar view tab index has to be computed since it has the same path as the multiple calendar view
const getCurrentTabIndex = ({ calendarActor, hasManagementRight, hasCalendarNavigateRight }) => {
  const tabs = getStaticTabList(hasManagementRight, hasCalendarNavigateRight);

  const tabIndex = tabs.findIndex((tab) => {
    const routes = tab.routes || [tab.href];
    return routes.includes(window.location.pathname);
  });

  const multiCalendarTabIndex = tabs.findIndex(tab => tab.type === TAB_TYPES.MULTI_CALENDAR);
  if (tabIndex === multiCalendarTabIndex && calendarActor && hasManagementRight) {
    return tabs.length;
  }

  return tabIndex;
};

const CalendarSidePanel = ({ rtcServerUrl }) => {
  const dispatch = useDispatch();

  const isExtended = useSelector((state) => state.sidePanel.isExtended);
  const userId = useSelector((state) => state.user.id);
  const hasManagementRight = useSelector((state) => selectHasRight(state, RIGHTS.CALENDAR_MANAGEMENT));
  const hasServiceListRight = useSelector((state) => selectHasRight(state, RIGHTS.SERVICES_LIST));
  const hasCalendarNavigateRight = useSelector((state) => selectHasRight(state, RIGHTS.CALENDAR_NAVIGATE));
  const hasAppointmentCreateRight = useSelector((state) => selectHasRight(state, RIGHTS.APPOINTMENTS_CREATE));
  const calendarActor = useSelector(selectCalendarActor);
  const isSingleView = useSelector(state => state.calendar.actorId && state.calendar.actorType);

  // Filtered list management, a little overkill but neeeded to work with calendar navigation.
  const expertList = useSelector(selectFilteredManagedExperts);
  const totalExpertCount = useSelector(unfilteredManagedExpertCount);
  const expertFilterText = useSelector((state) => state.expert.filterText);
  const resourceList = useSelector(selectFilteredResources);
  const totalResourceCount = useSelector(selectTotalResources);
  const resourceFilterText = useSelector((state) => state.resource.filterText);

  // Memoize to prevent avatar re-renders
  const handleItemClick = useCallback((actorId, actorType) => {
    const tabs = getStaticTabList(hasManagementRight, hasCalendarNavigateRight);
    setCurrentTabIndex(tabs.length);
    dispatch(goToActorCalendar({ actorId, actorType }));
  }, [hasManagementRight, hasCalendarNavigateRight]);

  // Memoize to prevent avatar re-renders
  const handleSettingsClick = useCallback((actorId, actorType) => {
    dispatch(goToActorSettings({ actorId, actorType }));
  }, []);

  // Memoize to prevent avatar re-renders
  const handleBookClick = useCallback((id) => {
    dispatch(resetBookingForm());
    dispatch(goToGivenExpertForm({ id }));
    // The side panel is also displayed on the calendar settings and appointment
    // list pages, which do not mount the toolbar holding the modal. Navigate to
    // the calendar first so that the modal has somewhere to open.
    if (window.location.pathname !== CALENDAR_PATH) {
      $.pjax({ url: CALENDAR_PATH, container: PJAX_MAIN_CONTAINER_SELECTOR });
    }
    dispatch(toggleAppointmentModal({ isOpen: true }));
  }, []);


  // Tab management
  const [currentTabIndex, setCurrentTabIndex] = useState(getCurrentTabIndex({
    calendarActor,
    hasManagementRight,
    hasCalendarNavigateRight,
  }));

  const handleTabClick = (event, newTabIndex) => {
    dispatch(resetBookingForm());

    const tabs = getStaticTabList(hasManagementRight, hasCalendarNavigateRight);
    const clickedTab = tabs[newTabIndex];

    if (clickedTab?.type === TAB_TYPES.MULTI_CALENDAR) {
      if (hasManagementRight) {
        dispatch(goToMultipleCalendar());
      } else {
        dispatch(goToActorCalendar({ actorId: userId, actorType: EXPERT }));
      }
    }
    if (isSpecialKey(event)) {
      return;
    }
    setCurrentTabIndex(newTabIndex);
  };
  useEffect(() => {
    setCurrentTabIndex(getCurrentTabIndex({
      calendarActor,
      hasManagementRight,
      hasCalendarNavigateRight,
    }));
  }, [calendarActor, hasCalendarNavigateRight]);

  return (
    <SidePanel>
      <HighlightedAppointment canExtend hasMargin rtcServerUrl={rtcServerUrl} />
      <Tabs
        orientation='vertical'
        value={currentTabIndex}
        sx={{ width: '100%' }}
      >
        {getStaticTabList(hasManagementRight, hasCalendarNavigateRight).map(({ label, icon, href }, index) => {
          return (
            href &&
              <Tab
                key={label}
                component='a'
                label={isExtended && <span className='calendarSidePanel-tabLabel'>{label}</span>}
                href={href}
                data-pjax
                wrapped
                className='calendarSidePanel-tab'
                icon={icon}
                onClick={(event) => handleTabClick(event, index)}
              />
          );
        })}
        {calendarActor && hasManagementRight && (
          <Tab
            component='a'
            label={isExtended && <span className='calendarSidePanel-tabLabel'>{`${i18n._('Calendar of')} ${calendarActor.alias}`}</span>}
            href={CALENDAR_PATH}
            data-pjax
            wrapped
            className='calendarSidePanel-tab'
            onClick={(event) => handleTabClick(event, getStaticTabList(hasManagementRight, hasCalendarNavigateRight).length)}
            icon={
              <InitialsAvatar
                picture={calendarActor.picture}
                initials={getInitials(calendarActor.alias)}
                sideSize={24}
                sx={{ marginRight: 1 }}
              />
            }
          />
        )}
      </Tabs>
      {hasManagementRight && (
        <>
          <CalendarList
            title={hasServiceListRight && i18n._('Practitioners')}
            titleIcon={<PeopleIcon />}
            itemTooltipTitle={i18n._('Edit the practitioner’s calendar')}
            infoText={i18n._('Click a practitioner’s name to edit their calendar.')}
            emptyListText={i18n._('No practitioner has delegated their calendar to you.')}
            filterText={expertFilterText}
            itemList={expertList}
            onItemClick={(actorId) => handleItemClick(actorId, EXPERT)}
            onSettingsClick={(actorId) => handleSettingsClick(actorId, EXPERT)}
            onBookClick={hasAppointmentCreateRight ? (actorId) => handleBookClick(actorId) : null}
            onFilterChange={(filterText) => dispatch(changeExpertFilterText(filterText))}
            initialIsOpen
            noMultipleCalendar={isSingleView}
            shouldHideSearch={totalExpertCount < CALENDAR_MINIMUM_ITEMS_TO_SHOW_SEARCH}
          />
          {hasServiceListRight && (
            <CalendarList
              title={i18n._('Mobil units')} // Using a specific name for the moment because it's the only usage.
              titleIcon={<AmbulanceIcon />}
              itemTooltipTitle={i18n._('Edit the resource’s calendar')}
              infoText={i18n._('Click on a mobile resource name to modify its calendar.')}
              emptyListText={i18n._('There are no resource to display.')}
              filterText={resourceFilterText}
              itemList={resourceList}
              onItemClick={(actorId) => handleItemClick(actorId, RESOURCE)}
              onSettingsClick={(actorId) => handleSettingsClick(actorId, RESOURCE)}
              onFilterChange={(filterText) => dispatch(changeResourceFilterText(filterText))}
              initialIsOpen
              noMultipleCalendar
              shouldHideSearch={totalResourceCount < CALENDAR_MINIMUM_ITEMS_TO_SHOW_SEARCH}
            />
          )}
        </>
      )}
    </SidePanel>
  );
};

export default CalendarSidePanel;
